import React, { FC, ReactNode, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTypedSelector } from '../../../store/store';
import {
  HomeShell,
  Column,
  LogoRow,
  LogoImage,
  LogoSkeleton,
  LogoFallback,
  Tagline,
  TicketLabel,
  TicketOuter,
  TicketCard,
  Perforation,
} from '../HomeScreen/HomeScreen.styles';

/**
 * The video ticket shared by every creator screen: the account header
 * (logo, tagline, "Your video ticket") and the cut paper card. The home
 * screen and the upload flow render their sections inside it.
 */

const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

const isComplete = (img: HTMLImageElement | null) => !!img && img.complete && img.naturalWidth > 0;

interface HeaderProps {
  onLogoLoad?: () => void;
  onLogoError?: () => void;
}

export const TicketHeader: FC<HeaderProps> = ({ onLogoLoad, onLogoError }) => {
  const {
    account: { logo, name },
  } = useTypedSelector((state) => state.account);
  const [logoError, setLogoError] = useState(false);
  const [logoLoaded, setLogoLoaded] = useState(false);
  const logoImgRef = useRef<HTMLImageElement | null>(null);

  // A cached logo can complete before React attaches onLoad; `load` then
  // never fires and the skeleton would stay.
  useEffect(() => {
    setLogoLoaded(isComplete(logoImgRef.current));
  }, [logo]);

  return (
    <>
      <LogoRow>
        {logo && !logoError ? (
          <>
            {!logoLoaded && <LogoSkeleton aria-hidden />}
            <LogoImage
              ref={logoImgRef}
              src={logo}
              alt={name || 'Company logo'}
              $ready={logoLoaded}
              onLoad={() => {
                setLogoLoaded(true);
                onLogoLoad?.();
              }}
              onError={() => {
                setLogoError(true);
                onLogoError?.();
              }}
            />
          </>
        ) : (
          <LogoFallback>{name}</LogoFallback>
        )}
      </LogoRow>

      <Tagline>Your experience, authentically</Tagline>

      <TicketLabel>Your video ticket</TicketLabel>
    </>
  );
};

/** Dashed rule between ticket sections; the paper cuts a notch beside it. */
export const TicketPerforation: FC = () => <Perforation data-perforation aria-hidden />;

interface PaperProps {
  children: ReactNode;
  /** Stretch the card to the bottom of the screen (upload flow). */
  fill?: boolean;
}

/**
 * The card's mask needs the y of each perforation (up to two) to cut the
 * side notches. Measured after layout and again whenever the card or one of
 * its sections resizes (fonts loading, orientation, a step changing height),
 * or sections are added or removed.
 */
export const TicketPaper: FC<PaperProps> = ({ children, fill }) => {
  const cardRef = useRef<HTMLDivElement | null>(null);

  useIsomorphicLayoutEffect(() => {
    const card = cardRef.current;
    if (!card) return undefined;
    const measure = () => {
      const perfs = card.querySelectorAll<HTMLElement>('[data-perforation]');
      ['--n1', '--n2'].forEach((name, i) => {
        card.style.setProperty(name, perfs[i] ? `${perfs[i].offsetTop + 1}px` : '-999px');
      });
    };
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    const observeAll = () => {
      ro?.disconnect();
      ro?.observe(card);
      Array.from(card.children).forEach((child) => ro?.observe(child));
      measure();
    };
    observeAll();
    const mo = typeof MutationObserver !== 'undefined' ? new MutationObserver(observeAll) : null;
    mo?.observe(card, { childList: true });
    return () => {
      ro?.disconnect();
      mo?.disconnect();
    };
  }, []);

  return (
    <TicketOuter sx={fill ? { flex: 1, display: 'flex', flexDirection: 'column' } : undefined}>
      <TicketCard ref={cardRef} sx={fill ? { flex: 1 } : undefined}>
        {children}
      </TicketCard>
    </TicketOuter>
  );
};

interface PageProps extends HeaderProps {
  children: ReactNode;
  /** Let the ticket fill the remaining screen height. */
  fill?: boolean;
}

/** Page shell + header; the ticket card goes in `children`. */
export const TicketPage: FC<PageProps> = ({ children, fill, onLogoLoad, onLogoError }) => (
  <HomeShell sx={fill ? { minHeight: 'var(--app-height, 100vh)' } : undefined}>
    <Column sx={fill ? { flex: 1 } : undefined}>
      <TicketHeader onLogoLoad={onLogoLoad} onLogoError={onLogoError} />
      {children}
    </Column>
  </HomeShell>
);
